import { useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { AD_BUTTONS, ALTS_MAX, CARDS_MAX, CARDS_MIN, type AdCard } from "../../../shared/goals/ready";
import { PhoneNav } from "../../../shared/phone/PhoneNav";
import { PnRow, Segmented } from "../../../shared/phone/Row";
import { ActionSheet, Sheet } from "../../../shared/phone/Sheet";
import { useRail } from "../../../shared/phone/media";
import { Icon } from "../../components/ui/Icon";
import { ApiError } from "../../lib/api";
import { readyApi, type AdWords, type ReadyAd } from "../../lib/readyApi";
import { FORMATS, postDay } from "./AdItem";
import { AdPhone, FeedCard, type PreviewView } from "./AdPreview";
import type { AdsCtx } from "./AdsNative";
import { fitOf, scriptLines } from "./ScriptLines";
import { ScriptReader } from "./ScriptReader";
import { canRecordHere } from "./useRecorder";

/**
 * One ad on a phone and an iPad: Feed or Stories and Reels above the ad drawn as people will see it, then its
 * video, its words, its button and where it opens as rows that open sheets. An ad without its video says so,
 * with the way to add it. Remove this ad sits alone at the bottom, and asks first.
 */

const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");
const MEDIA_TYPES = "video/mp4,video/quicktime,image/jpeg,image/png,image/webp";
const PHOTO_TYPES = "image/jpeg,image/png,image/webp";
const ORIGIN: Record<string, string> = { video: "Your video", post: "Your post", owner: "Your idea", plan: "Idea from the plan", agent: "Idea from the plan" };
const newCardId = () => `c${Math.random().toString(36).slice(2, 10)}`;
// Why: the ad is drawn again after every save; the preview it showed (feed or Stories and Reels) stays.
const shown = new Map<string, PreviewView>();

const isImage = (ad: ReadyAd) => (ad.asset?.contentType ?? "").startsWith("image/") || ad.words.format === "image";

/** The ad's state as a pill: running, ready, a file to add, or the agent writing its words. */
export function adPill(ad: ReadyAd): ReactNode {
  const [tone, label] =
    ad.status === "launched"
      ? ["good", "Running"]
      : ad.status === "drafting"
        ? ["violet", "Writing the words"]
        : ad.status === "ready"
          ? ["acc", "Ready"]
          : ad.words.format === "carousel"
            ? ["warn", "Needs its photos"]
            : ["warn", isImage(ad) ? "Needs the photo" : "Needs the video"];
  return (
    <span className={`pn-pill ${tone} ad-pill`}>
      <i />
      {label}
    </span>
  );
}

type WordsField = "primary" | "headline";

export function AdScreen({ c, ad, recordTall, onWhere }: { c: AdsCtx; ad: ReadyAd; recordTall: boolean; onWhere: () => void }) {
  const navigate = useNavigate();
  const ipad = useRail();
  const { goalId, product } = c;
  const w = ad.words;
  const format = w.format ?? "camera";
  const single = format !== "carousel" && format !== "post";
  const camera = format === "camera";
  const image = isImage(ad);
  const live = ad.status === "launched";
  const wantsAlts = w.wants_alts ?? 0;
  const writing = ad.status === "drafting" && !(w.primary && wantsAlts > 0);
  const stoppedWhy = c.ready.runs.ads && ["failed", "cancelled"].includes(c.ready.runs.ads.state) ? (c.ready.runs.ads.error ?? "") : null;
  const [view, setViewState] = useState<PreviewView>(() => (recordTall ? "stories" : (shown.get(ad.id) ?? "feed")));
  const setView = (v: PreviewView) => {
    shown.set(ad.id, v);
    setViewState(v);
  };
  const [busy, setBusy] = useState(false);
  const [sheet, setSheet] = useState<"words" | "button" | "alts" | "script" | "remove" | "removeTall" | null>(null);
  const [field, setField] = useState<WordsField>("primary");
  const [card, setCard] = useState<AdCard | null>(null);
  const [reading, setReading] = useState<{ lines: string[]; to: "main" | "tall" | "auto"; camera: boolean } | null>(null);
  const file = useRef<HTMLInputElement>(null);
  const tallFile = useRef<HTMLInputElement>(null);
  const cardFile = useRef<HTMLInputElement>(null);
  const cardFor = useRef<string | null>(null);
  const lines = scriptLines(w.says ?? "");

  // biome-ignore lint/correctness/useExhaustiveDependencies: only as the ad first shows, from the code's address.
  useEffect(() => {
    if (!recordTall || !camera || !ad.asset) return;
    setReading({ lines, to: "tall", camera: true });
    navigate(`/goals/${goalId}/ads?ad=${encodeURIComponent(ad.id)}`, { replace: true });
  }, []);

  const act = async (f: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await f();
      await c.load();
    } catch (e) {
      c.say(message(e), false);
      await c.load();
    } finally {
      setBusy(false);
    }
  };
  // Why: null clears a word on the server, as on a computer; undefined would leave it.
  const save = (words: Record<string, unknown>) => act(() => readyApi.editAd(goalId, ad.id, ad.rev, words as Partial<AdWords>));
  const upload = (f: File, slot: string) => void act(() => readyApi.upload(goalId, ad.id, ad.rev, f, slot));
  const cards = w.cards ?? [];
  const cta = w.cta ?? "SIGN_UP";
  const origin = ad.origin === "video" && format === "carousel" ? "Your photos" : ad.origin === "video" && image ? "Your photo" : (ORIGIN[ad.origin] ?? "Your idea");
  const back = { to: `/goals/${goalId}/ads`, label: "Ads" };
  const writingNote = writing ? (
    stoppedWhy !== null ? (
      `The agent stopped before writing these words. ${stoppedWhy}`.trim()
    ) : (
      <>
        <span className="ad-spin dk" />
        Writing the words
      </>
    )
  ) : undefined;

  // --- The preview ---
  const stage = (
    <>
      {single && (
        <Segmented
          label="Where it shows"
          value={view}
          onChange={setView}
          options={[
            { value: "feed", label: "Feed" },
            { value: "stories", label: "Stories and Reels" },
          ]}
        />
      )}
      <div className="ad-stage">
        <AdPhone goalId={goalId} ad={ad} product={product} opens={c.opens} view={single ? view : "feed"} writing={writingNote} />
      </div>
    </>
  );

  // --- The video or photos ---
  let media: ReactNode = null;
  if (single && !live) {
    const tallView = view === "stories";
    media = (
      <section>
        <div className="pn-cap">{tallView ? "Stories and Reels" : image ? "Photo" : "Video"}</div>
        <div className="pn-grp">
          {!tallView ? (
            <>
              {ad.asset ? (
                <PnRow icon={<Icon name="check" />} iconTone="good" title={ad.origin === "video" ? (image ? "Your photo" : "Your video") : "Added"} sub="Shows in the feed" />
              ) : (
                <PnRow icon={<Icon name="film" />} iconTone="warn" title={image ? "Needs the photo" : "Needs the video"} sub={`It runs once the ${image ? "photo" : "video"} is in`} />
              )}
              {camera && (
                <PnRow
                  className={ad.asset ? undefined : "accent"}
                  onClick={() => setReading({ lines, to: "main", camera: true })}
                  icon={<Icon name="adCam" />}
                  title={ad.asset ? "Record again with the script" : "Record with the script"}
                  sub={lines.length ? `${lines.length} lines, about ${fitOf(lines).seconds} seconds` : "Your camera, with the words rolling"}
                  chevron
                  wrap
                />
              )}
              <PnRow onClick={() => file.current?.click()} icon={<Icon name="upload" />} iconTone="plain" title={ad.asset ? "Replace" : image ? "Add the photo" : "Add the video"} chevron={false} />
            </>
          ) : !ad.asset ? (
            <PnRow icon={<Icon name="film" />} iconTone="plain" title="Add the feed version first" sub="The upright version comes after it." wrap />
          ) : (
            <>
              {ad.files.tall ? (
                <PnRow icon={<Icon name="check" />} iconTone="good" title="Added" sub="The upright version, 9:16" />
              ) : (
                <PnRow icon={<Icon name="phone" />} iconTone="plain" title="No upright version yet" sub="Meta shows the wide one here, with space above and below" wrap />
              )}
              {camera && <PnRow onClick={() => setReading({ lines, to: "tall", camera: true })} icon={<Icon name="adCam" />} title={ad.files.tall ? "Record it again" : "Record it upright"} chevron />}
              <PnRow onClick={() => tallFile.current?.click()} icon={<Icon name="upload" />} iconTone="plain" title={ad.files.tall ? "Replace" : image ? "Add the upright photo" : "Add the upright version"} chevron={false} />
              {ad.files.tall && <PnRow onClick={() => setSheet("removeTall")} title="Remove the upright version" danger />}
            </>
          )}
        </div>
      </section>
    );
  }
  if (format === "carousel")
    media = (
      <section>
        <div className="pn-cap">The cards</div>
        <div className="pn-grp">
          {cards.map((x, i) => (
            <PnRow
              key={x.id}
              onClick={live ? undefined : () => setCard(x)}
              icon={
                <span className={ad.files.cards[x.id] ? "ad-th has" : "ad-th"}>
                  {ad.files.cards[x.id] ? <img src={readyApi.videoUrl(goalId, ad.id, ad.rev, `card:${x.id}`)} alt="" /> : <Icon name="photo" />}
                </span>
              }
              className="ad-adr"
              title={x.headline || `Card ${i + 1}`}
              sub={x.description || (ad.files.cards[x.id] ? "" : "Needs its photo")}
              chevron={!live}
            />
          ))}
          {!live && cards.length < CARDS_MAX && (
            <PnRow className="accent" onClick={() => void save({ cards: [...cards, { id: newCardId(), headline: "", description: "" }] })} icon={<Icon name="plus" />} title="Add a card" chevron={false} />
          )}
        </div>
        <p className="pn-note">Square photos, 1080 by 1080.</p>
      </section>
    );
  if (format === "post" && w.post)
    media = (
      <section>
        <div className="pn-cap">The post</div>
        <div className="pn-grp">
          <PnRow onClick={live ? undefined : () => c.setPicking(ad)} title={`Published ${postDay(w.post.at)}`} sub={w.post.text || w.post.kind} value={live ? undefined : "Change"} chevron={!live} wrap />
        </div>
        <p className="pn-note">Its words, likes and comments stay as they are on Facebook.</p>
      </section>
    );

  // --- The words ---
  const words = writing ? (
    <section>
      <div className="pn-grp">
        <div className="pn-row static wrap">
          {stoppedWhy === null && <span className="ad-spin" aria-hidden="true" />}
          <span className="m">
            <span className="t">{stoppedWhy === null ? "Writing the words" : "The words were not written"}</span>
            <span className="s">{stoppedWhy === null ? "The agent writes them from the plan" : `The agent stopped before writing these words. ${stoppedWhy}`.trim()}</span>
          </span>
        </div>
        {stoppedWhy !== null && <PnRow className="accent" onClick={() => void c.act(() => readyApi.draftAgain(goalId, "ads"), "The agent is writing the words again.")} title="Write the words again" chevron={false} />}
      </div>
    </section>
  ) : (
    <>
      <section>
        <div className="pn-cap">Words</div>
        <div className="pn-grp">
          {format !== "post" && (
            <PnRow
              className="ad-wr"
              onClick={
                live
                  ? undefined
                  : () => {
                      setField("primary");
                      setSheet("words");
                    }
              }
              title="Ad text"
              sub={w.primary ?? ""}
              chevron={!live}
              wrap
            />
          )}
          {single && (
            <PnRow
              onClick={
                live
                  ? undefined
                  : () => {
                      setField("headline");
                      setSheet("words");
                    }
              }
              title="Headline"
              sub={w.headline ?? ""}
              chevron={!live}
              wrap
            />
          )}
          <PnRow onClick={live ? undefined : () => setSheet("button")} title="Button" value={AD_BUTTONS[cta]} chevron={!live} />
          <PnRow onClick={live ? undefined : onWhere} title="Opens" value={<span className="ad-opv">{c.opens || "Not set yet"}</span>} chevron={!live} />
        </div>
      </section>
      {single && !live && (
        <section>
          <div className="pn-grp">
            <PnRow onClick={() => setSheet("alts")} icon={<Icon name="adSwap" />} iconTone="violet" title="Other words Meta can try" value={(w.alts?.length ?? 0) + wantsAlts ? `${(w.alts?.length ?? 0) + wantsAlts} of ${ALTS_MAX}` : "None yet"} chevron wrap />
          </div>
        </section>
      )}
      {camera && !live && (
        <section>
          <div className="pn-grp">
            {w.says !== undefined || ad.touched ? (
              <PnRow onClick={() => setSheet("script")} icon={<Icon name="doc" />} iconTone="plain" title="Your script" sub={lines.length ? fitOf(lines).words : "Write what you say, a line at a time"} chevron wrap />
            ) : (
              <PnRow onClick={() => void act(() => readyApi.askScript(goalId, ad.id))} icon={<Icon name="spark" />} iconTone="violet" title="Draft a script from my idea" sub="Optional: words to read on camera" chevron={false} wrap />
            )}
          </div>
        </section>
      )}
      {format !== "post" && !live && (
        <section>
          <div className="pn-grp">
            <button
              type="button"
              className="pn-row"
              role="switch"
              aria-checked={!!w.ai}
              disabled={busy}
              onClick={() => void save({ ai: w.ai ? null : true })}
            >
              <span className="m">
                <span className="t">Made or edited with AI</span>
              </span>
              <span className="pn-sw" aria-checked={!!w.ai} aria-hidden="true" />
            </button>
          </div>
        </section>
      )}
    </>
  );

  const rows = (
    <>
      {media}
      {words}
      {w.why && (ad.origin === "plan" || ad.origin === "agent") && (
        <p className="pn-note ad-whyi">
          <b>Why this idea:</b> {w.why}
        </p>
      )}
      {!live && (
        <section>
          <div className="pn-grp">
            <PnRow onClick={() => setSheet("remove")} title="Remove this ad" danger />
          </div>
        </section>
      )}
    </>
  );

  return (
    <div className="pn-screen ad-scr ad-one">
      <PhoneNav
        title={w.name ?? w.idea ?? "Your ad"}
        large
        back={back}
        sub={
          <span className="ad-tags">
            {adPill(ad)}
            <span>
              {origin} · {FORMATS.find(([k]) => k === format)?.[1]}
            </span>
          </span>
        }
      />
      {ipad ? (
        <div className="ad-adgrid">
          <div className="ad-adl">{stage}</div>
          <div className="pn-stack tight ad-adr2">{rows}</div>
        </div>
      ) : (
        <div className="pn-stack tight">
          {stage}
          {rows}
        </div>
      )}

      <input
        ref={file}
        type="file"
        hidden
        accept={MEDIA_TYPES}
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (f) upload(f, "main");
        }}
      />
      <input
        ref={tallFile}
        type="file"
        hidden
        accept={image ? PHOTO_TYPES : "video/mp4,video/quicktime"}
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (f) upload(f, "tall");
        }}
      />
      <input
        ref={cardFile}
        type="file"
        hidden
        accept={PHOTO_TYPES}
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (f && cardFor.current) upload(f, `card:${cardFor.current}`);
        }}
      />

      <WordsSheet
        open={sheet === "words"}
        onClose={() => setSheet(null)}
        field={field}
        c={c}
        ad={ad}
        onSave={async (v) => {
          await save({ [field]: v });
          return true;
        }}
      />
      <Sheet open={sheet === "button"} onClose={() => setSheet(null)} title="Button">
        <div className="pn-grp">
          {Object.entries(AD_BUTTONS).map(([k, l]) => (
            <button
              key={k}
              type="button"
              className="pn-row"
              onClick={() => {
                setSheet(null);
                if (k !== cta) void save({ cta: k as keyof typeof AD_BUTTONS });
              }}
            >
              <span className="m">
                <span className="t">{l}</span>
              </span>
              {k === cta && (
                <span className="ad-tick">
                  <Icon name="check" />
                </span>
              )}
            </button>
          ))}
        </div>
      </Sheet>
      <AltsSheet open={sheet === "alts"} onClose={() => setSheet(null)} c={c} ad={ad} busy={busy} act={act} save={save} stoppedWhy={stoppedWhy} />
      <ScriptSheet
        open={sheet === "script"}
        onClose={() => setSheet(null)}
        says={w.says ?? ""}
        onSave={async (says) => {
          await save({ says: says || null });
          return true;
        }}
        onRead={(ls) => {
          setSheet(null);
          setReading({ lines: ls, to: "auto", camera: false });
        }}
      />
      <CardSheet
        card={card}
        index={card ? cards.findIndex((x) => x.id === card.id) : -1}
        canRemove={cards.length > CARDS_MIN}
        hasPhoto={!!(card && ad.files.cards[card.id])}
        onClose={() => setCard(null)}
        onPhoto={() => {
          if (!card) return;
          cardFor.current = card.id;
          cardFile.current?.click();
        }}
        onSave={async (next) => {
          await save({ cards: cards.map((x) => (x.id === next.id ? next : x)) });
          return true;
        }}
        onRemove={() => card && void save({ cards: cards.filter((x) => x.id !== card.id) })}
      />
      <ActionSheet
        open={sheet === "remove"}
        onClose={() => setSheet(null)}
        title="Remove this ad?"
        message={`“${w.name ?? "This ad"}” leaves this goal.`}
        actions={[
          {
            label: "Remove this ad",
            danger: true,
            onSelect: () =>
              void c.act(async () => {
                await readyApi.removeIdea(goalId, ad.id);
                c.removedAd(ad.id);
                navigate(back.to, { replace: true });
              }),
          },
        ]}
      />
      <ActionSheet
        open={sheet === "removeTall"}
        onClose={() => setSheet(null)}
        title="Remove the upright version?"
        message="Meta then shows the wide one in Stories and Reels."
        actions={[{ label: "Remove it", danger: true, onSelect: () => void act(() => readyApi.removeTall(goalId, ad.id, ad.rev)) }]}
      />
      {reading && (
        <ScriptReader
          lines={reading.lines}
          canRecord={canRecordHere()}
          startCamera={reading.camera}
          slotFor={(wide) => (reading.to === "main" ? "main" : reading.to === "tall" ? (wide ? null : "tall") : "main")}
          onUse={async (take, slot) => {
            await readyApi.upload(goalId, ad.id, ad.rev, take, slot);
            if (slot === "tall") setView("stories");
            await c.load();
          }}
          onClose={() => setReading(null)}
        />
      )}
    </div>
  );
}

/** The ad text or the headline in a sheet, with the ad drawn above it so the change shows as it is typed. */
function WordsSheet({ open, onClose, field, c, ad, onSave }: { open: boolean; onClose: () => void; field: WordsField; c: AdsCtx; ad: ReadyAd; onSave: (v: string) => Promise<boolean> }) {
  const was = (field === "primary" ? ad.words.primary : ad.words.headline) ?? "";
  const [v, setV] = useState(was);
  const [busy, setBusy] = useState(false);
  const box = useRef<HTMLTextAreaElement & HTMLInputElement>(null);
  useEffect(() => {
    if (!open) return;
    setV(was);
    requestAnimationFrame(() => {
      const el = box.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    });
  }, [open, was]);
  const text = field === "primary";
  const save = async () => {
    const t = v.trim();
    if (!t) return;
    if (t === was) return onClose();
    setBusy(true);
    const ok = await onSave(t);
    setBusy(false);
    if (ok) onClose();
  };
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={text ? "Ad text" : "Headline"}
      className="ad-wsh"
      left={
        <button type="button" className="pn-tbtn" onClick={onClose}>
          Cancel
        </button>
      }
      right={
        <button type="button" className="pn-tbtn strong" disabled={busy || !v.trim()} onClick={() => void save()}>
          Save
        </button>
      }
    >
      <div className="ad-wpv">
        <FeedCard goalId={c.goalId} ad={ad} product={c.product} opens={c.opens} text={text ? v : (ad.words.primary ?? "")} headline={text ? (ad.words.headline ?? "") : v} mini />
      </div>
      <div className="pn-form">
        {text ? (
          <textarea ref={box} className="pn-inp ad-17" rows={4} aria-label="Ad text" value={v} onChange={(e) => setV(e.target.value)} />
        ) : (
          <input ref={box} className="pn-inp ad-17" aria-label="Headline" maxLength={60} value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === "Enter" && void save()} />
        )}
        <p className="ad-hint">
          {v.length} characters. {text ? "About 125 work best." : "27 to 40 work best."}
        </p>
      </div>
    </Sheet>
  );
}

/** The other words Meta can try beside the ad's own: each to change or remove, and more to ask the agent for. */
function AltsSheet({
  open,
  onClose,
  c,
  ad,
  busy,
  act,
  save,
  stoppedWhy,
}: {
  open: boolean;
  onClose: () => void;
  c: AdsCtx;
  ad: ReadyAd;
  busy: boolean;
  act: (f: () => Promise<unknown>) => Promise<void>;
  save: (w: Record<string, unknown>) => Promise<void>;
  stoppedWhy: string | null;
}) {
  const alts = ad.words.alts ?? [];
  const wants = ad.words.wants_alts ?? 0;
  const [edit, setEdit] = useState<number | null>(null);
  const [d, setD] = useState({ primary: "", headline: "" });
  useEffect(() => {
    if (!open) setEdit(null);
  }, [open]);
  const begin = (i: number) => {
    setD({ ...alts[i] });
    setEdit(i);
  };
  if (edit !== null)
    return (
      <Sheet
        open={open}
        onClose={onClose}
        title={`Other words ${edit + 1}`}
        left={
          <button type="button" className="pn-tbtn" onClick={() => setEdit(null)}>
            Back
          </button>
        }
        right={
          <button
            type="button"
            className="pn-tbtn strong"
            disabled={busy || !d.primary.trim() || !d.headline.trim()}
            onClick={() => {
              const next = alts.map((x, k) => (k === edit ? { primary: d.primary.trim(), headline: d.headline.trim() } : x));
              setEdit(null);
              void save({ alts: next });
            }}
          >
            Save
          </button>
        }
      >
        <div className="pn-form">
          <div className="pn-field">
            <label htmlFor="ad-alt-p">Ad text</label>
            <textarea id="ad-alt-p" className="pn-inp ad-17" rows={3} value={d.primary} onChange={(e) => setD({ ...d, primary: e.target.value })} />
          </div>
          <div className="pn-field">
            <label htmlFor="ad-alt-h">Headline</label>
            <input id="ad-alt-h" className="pn-inp ad-17" maxLength={60} value={d.headline} onChange={(e) => setD({ ...d, headline: e.target.value })} />
          </div>
        </div>
        <div className="pn-grp ad-mt">
          <PnRow
            title="Remove these words"
            danger
            onClick={() => {
              const next = alts.filter((_, k) => k !== edit);
              setEdit(null);
              void save({ alts: next.length ? next : null });
            }}
          />
        </div>
      </Sheet>
    );
  return (
    <Sheet open={open} onClose={onClose} title="Other words Meta can try">
      <p className="ad-shs">Meta mixes these with your own words and shows what works. Up to {ALTS_MAX}.</p>
      <div className="pn-grp">
        {alts.map((x, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: other words have no id; their place is their identity.
          <PnRow key={i} onClick={wants > 0 ? undefined : () => begin(i)} title={x.headline} sub={x.primary} chevron={wants === 0} wrap />
        ))}
        {Array.from({ length: wants }, (_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: placeholders for words still being written.
          <div className="pn-row static wrap" key={`w${i}`}>
            {stoppedWhy === null && <span className="ad-spin" aria-hidden="true" />}
            <span className="m">
              <span className="t">{stoppedWhy === null ? "Writing it" : "Not written"}</span>
              {stoppedWhy !== null && <span className="s">{`The agent stopped before writing these words. ${stoppedWhy}`.trim()}</span>}
            </span>
          </div>
        ))}
        {wants > 0 && stoppedWhy !== null && <PnRow className="accent" onClick={() => void c.act(() => readyApi.draftAgain(c.goalId, "ads"), "The agent is writing the words again.")} title="Write the words again" chevron={false} />}
        {alts.length + wants < ALTS_MAX && wants === 0 && <PnRow className="accent" onClick={() => void act(() => readyApi.askAlts(c.goalId, ad.id))} icon={<Icon name="plus" />} title="Ask for other words" chevron={false} />}
      </div>
    </Sheet>
  );
}

/** The script to read on camera, a line per spoken line, timed against the ad's 15 to 30 seconds. */
function ScriptSheet({ open, onClose, says, onSave, onRead }: { open: boolean; onClose: () => void; says: string; onSave: (s: string) => Promise<boolean>; onRead: (lines: string[]) => void }) {
  const [v, setV] = useState(says);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (open) {
      setV(scriptLines(says).join("\n"));
      setCopied(false);
    }
  }, [open, says]);
  const lines = scriptLines(v);
  const fit = fitOf(lines);
  const changed = lines.join("\n") !== scriptLines(says).join("\n");
  const save = async () => {
    if (!changed) return onClose();
    setBusy(true);
    const ok = await onSave(lines.join("\n"));
    setBusy(false);
    if (ok) onClose();
  };
  return (
    <Sheet
      open={open}
      onClose={onClose}
      tall
      title="Your script"
      left={
        <button type="button" className="pn-tbtn" onClick={onClose}>
          Cancel
        </button>
      }
      right={
        <button type="button" className="pn-tbtn strong" disabled={busy} onClick={() => void save()}>
          {changed ? "Save" : "Done"}
        </button>
      }
      foot={
        <div className="pn-btns">
          <button
            type="button"
            className="pn-btn"
            disabled={!lines.length}
            onClick={() =>
              void navigator.clipboard?.writeText(lines.join("\n")).then(
                () => setCopied(true),
                () => setCopied(false),
              )
            }
          >
            {copied ? "Copied" : "Copy"}
          </button>
          <button type="button" className="pn-btn pri" disabled={!lines.length || changed} onClick={() => onRead(lines)}>
            <Icon name="play" />
            Read it
          </button>
        </div>
      }
    >
      <p className={fit.over ? "ad-fit over" : "ad-fit"}>
        <i />
        {fit.words}
      </p>
      <div className="pn-form">
        <textarea className="pn-inp ad-17 ad-script" rows={9} aria-label="Your script, a line for each thing you say" value={v} onChange={(e) => setV(e.target.value)} />
        <p className="ad-hint">A line for each thing you say. The words roll a line at a time.</p>
      </div>
    </Sheet>
  );
}

/** One card of an ad people swipe: its photo, headline and line. */
function CardSheet({
  card,
  index,
  canRemove,
  hasPhoto,
  onClose,
  onPhoto,
  onSave,
  onRemove,
}: {
  card: AdCard | null;
  index: number;
  canRemove: boolean;
  hasPhoto: boolean;
  onClose: () => void;
  onPhoto: () => void;
  onSave: (c: AdCard) => Promise<boolean>;
  onRemove: () => void;
}) {
  const [d, setD] = useState({ headline: "", description: "" });
  const [confirm, setConfirm] = useState(false);
  useEffect(() => {
    if (card) setD({ headline: card.headline, description: card.description });
  }, [card]);
  return (
    <>
      <Sheet
        open={card !== null && !confirm}
        onClose={onClose}
        title={`Card ${index + 1}`}
        left={
          <button type="button" className="pn-tbtn" onClick={onClose}>
            Cancel
          </button>
        }
        right={
          <button
            type="button"
            className="pn-tbtn strong"
            onClick={async () => {
              if (!card) return;
              const next = { ...card, headline: d.headline.trim(), description: d.description.trim() };
              if (next.headline !== card.headline || next.description !== card.description) await onSave(next);
              onClose();
            }}
          >
            Save
          </button>
        }
      >
        <div className="pn-grp">
          <PnRow onClick={onPhoto} icon={<Icon name="photo" />} title={hasPhoto ? "Replace the photo" : "Add the photo"} chevron={false} />
        </div>
        <div className="pn-form ad-mt">
          <div className="pn-field">
            <label htmlFor="ad-card-h">Headline</label>
            <input id="ad-card-h" className="pn-inp" maxLength={60} value={d.headline} onChange={(e) => setD({ ...d, headline: e.target.value })} />
          </div>
          <div className="pn-field">
            <label htmlFor="ad-card-d">One line under it</label>
            <input id="ad-card-d" className="pn-inp" maxLength={80} value={d.description} onChange={(e) => setD({ ...d, description: e.target.value })} />
          </div>
        </div>
        {canRemove && (
          <div className="pn-grp ad-mt">
            <PnRow title="Remove this card" danger onClick={() => setConfirm(true)} />
          </div>
        )}
      </Sheet>
      <ActionSheet
        open={confirm}
        onClose={() => setConfirm(false)}
        title="Remove this card?"
        actions={[
          {
            label: "Remove this card",
            danger: true,
            onSelect: () => {
              onRemove();
              onClose();
            },
          },
        ]}
      />
    </>
  );
}
